import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ShieldCheck, Lock } from "lucide-react";
import { setToken } from "@/lib/adminAuth";
import { Logo } from "@/components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLogin() {
    const nav = useNavigate();
    const [step, setStep] = useState("credentials"); // credentials | 2fa
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [code, setCode] = useState("");
    const [loginToken, setLoginToken] = useState("");
    const [delivery, setDelivery] = useState("");
    const [loading, setLoading] = useState(false);

    const submitCreds = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const { data } = await axios.post(`${API}/admin/login`, {
                email: email.trim().toLowerCase(),
                password,
            });
            if (data.requires_2fa) {
                setLoginToken(data.login_token);
                setDelivery(data.delivery);
                setStep("2fa");
                toast.success(
                    data.delivery === "email"
                        ? "Code sent to your inbox"
                        : "Code generated (check backend logs)"
                );
            } else {
                setToken(data.token);
                nav("/admin");
            }
        } catch (err) {
            const detail = err.response?.data?.detail || "Login failed";
            toast.error(typeof detail === "string" ? detail : "Login failed");
        } finally {
            setLoading(false);
        }
    };

    const submit2FA = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const { data } = await axios.post(`${API}/admin/verify-2fa`, {
                login_token: loginToken,
                code: code.trim(),
            });
            setToken(data.token);
            toast.success("Welcome back.");
            nav("/admin");
        } catch (err) {
            const detail = err.response?.data?.detail || "Verification failed";
            toast.error(typeof detail === "string" ? detail : "Verification failed");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            data-testid="admin-login-page"
            className="min-h-screen bg-[#070A13] text-[#FDFBF7] grain flex flex-col"
        >
            <div className="max-w-3xl mx-auto w-full px-6 py-10">
                <div className="flex items-center gap-3">
                    <Logo className="w-9 h-9" dark />
                    <span className="font-display text-xl">The Raw Math</span>
                    <span className="ml-3 overline text-[#C5A059]">/ Admin</span>
                </div>
            </div>

            <div className="flex-1 flex items-center justify-center px-6 pb-20">
                <div className="w-full max-w-md">
                    <div className="mb-10 text-center">
                        <span className="inline-flex w-14 h-14 items-center justify-center rounded-full bg-[#C5A059]/10 border border-[#C5A059]/30 mb-6">
                            {step === "credentials" ? (
                                <Lock size={22} className="text-[#C5A059]" />
                            ) : (
                                <ShieldCheck size={22} className="text-[#C5A059]" />
                            )}
                        </span>
                        <h1 className="font-display text-3xl md:text-4xl tracking-tighter">
                            {step === "credentials"
                                ? "Admin sign-in"
                                : "Enter your code"}
                        </h1>
                        <p className="font-body text-sm text-white/50 mt-3">
                            {step === "credentials"
                                ? "Restricted. All attempts are logged."
                                : delivery === "email"
                                  ? `A 6-digit code was sent to ${email}. Expires in 10 minutes.`
                                  : "Delivery failed — check backend logs for the code."}
                        </p>
                    </div>

                    {step === "credentials" ? (
                        <form
                            onSubmit={submitCreds}
                            className="flex flex-col gap-5"
                            data-testid="admin-login-form"
                        >
                            <label className="flex flex-col gap-2">
                                <span className="overline text-white/50">Email</span>
                                <input
                                    data-testid="admin-email-input"
                                    type="email"
                                    required
                                    autoComplete="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="bg-[#11141D] border border-white/10 rounded-none px-4 py-3.5 font-body text-[#FDFBF7] focus:outline-none focus:border-[#C5A059] transition-colors"
                                />
                            </label>
                            <label className="flex flex-col gap-2">
                                <span className="overline text-white/50">
                                    Password
                                </span>
                                <input
                                    data-testid="admin-password-input"
                                    type="password"
                                    required
                                    autoComplete="current-password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="bg-[#11141D] border border-white/10 rounded-none px-4 py-3.5 font-body text-[#FDFBF7] focus:outline-none focus:border-[#C5A059] transition-colors"
                                />
                            </label>
                            <button
                                data-testid="admin-login-submit"
                                type="submit"
                                disabled={loading}
                                className="inline-flex items-center justify-center gap-2 bg-[#C5A059] text-[#070A13] font-body font-semibold rounded-full px-6 py-4 hover:bg-[#d9b675] transition-colors disabled:opacity-60"
                            >
                                {loading ? (
                                    <Loader2 size={18} className="animate-spin" />
                                ) : (
                                    "Continue"
                                )}
                            </button>
                        </form>
                    ) : (
                        <form
                            onSubmit={submit2FA}
                            className="flex flex-col gap-5"
                            data-testid="admin-2fa-form"
                        >
                            <label className="flex flex-col gap-2">
                                <span className="overline text-white/50">
                                    6-digit code
                                </span>
                                <input
                                    data-testid="admin-2fa-input"
                                    inputMode="numeric"
                                    pattern="[0-9]{6}"
                                    maxLength={6}
                                    required
                                    autoFocus
                                    value={code}
                                    onChange={(e) =>
                                        setCode(e.target.value.replace(/\D/g, ""))
                                    }
                                    className="bg-[#11141D] border border-white/10 rounded-none px-4 py-4 font-mono text-2xl text-center tracking-[0.5em] text-[#FDFBF7] focus:outline-none focus:border-[#C5A059] transition-colors"
                                />
                            </label>
                            <button
                                data-testid="admin-2fa-submit"
                                type="submit"
                                disabled={loading || code.length !== 6}
                                className="inline-flex items-center justify-center gap-2 bg-[#C5A059] text-[#070A13] font-body font-semibold rounded-full px-6 py-4 hover:bg-[#d9b675] transition-colors disabled:opacity-60"
                            >
                                {loading ? (
                                    <Loader2 size={18} className="animate-spin" />
                                ) : (
                                    "Verify & sign in"
                                )}
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    setStep("credentials");
                                    setCode("");
                                }}
                                className="font-mono text-xs uppercase tracking-[0.15em] text-white/40 hover:text-white/70"
                            >
                                ← Back
                            </button>
                        </form>
                    )}

                    <p className="mt-10 font-mono text-[10px] uppercase tracking-[0.15em] text-white/30 text-center">
                        HTTPS · bcrypt · JWT 8h · 5-attempts lockout · IP logged
                    </p>
                </div>
            </div>
        </div>
    );
}
